import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, InlineLoading, Modal } from "@carbon/react";
import { DocumentAdd } from "@carbon/icons-react";
import PageHeader from "../carbon/PageHeader/PageHeader";
import ConfirmDialog from "../carbon/ConfirmDialog/ConfirmDialog";
import {
  useDownloadDeliveryTemplate,
  useUploadDeliveryExcel,
} from "../../hooks/useDeliveries";
import { useNotification } from "../../context";
import { translateApiError } from "../../utils/translateApiError";
import "./newMassiveDeliveryForm.scss";

const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * The bulk-delivery-upload form (`/deliveries/bulk`), rebuilt on Carbon (Task 7, Deliveries
 * flow) in place of 299 lines of Tailwind and a hand-rolled `sr-only` dropzone.
 *
 * DELIBERATE DEVIATION (disclosed, and required by this task's brief): the spreadsheet picker
 * stays a native `<label htmlFor>` + real `<input type="file">`, the SAME pattern
 * `DeliveryFileField.tsx` (Task 6, this same flow) and `ImportFromFileModal.tsx` (PR import) use
 * — NOT Carbon's `FileUploaderDropContainer` (which the Suppliers flow's near-identical bulk form
 * adopted). Keeping the labelled native input preserves any e2e file interaction (`getByLabel` +
 * `setInputFiles`) exactly and still carries zero Tailwind — only the surrounding chrome moved to
 * Carbon tokens/classes.
 *
 * The Excel SSOT is untouched: `useDownloadDeliveryTemplate`/`useUploadDeliveryExcel`
 * (`src/hooks/useDeliveries.ts`) are called exactly as before — same blob download +
 * Content-Disposition filename parsing, same `axios.post("/deliveries/upload", formData, ...)`
 * multipart contract. Success/error notifications, the navigate-after-success, and the
 * disabled-submit gating (`isUploading || !fileName`) are preserved byte-for-byte in behavior;
 * only the presentation (Tailwind → Carbon) changed. RHF was dropped: the pre-Carbon form
 * registered a field with no resolver/validation rules, so `errors` was always empty — plain
 * `useState` carries the same (non-)validation with less indirection, matching what the
 * Suppliers flow's sibling bulk form (`NewMassiveSuppliersForm.tsx`) already established.
 */
const NewMassiveDeliveryForm = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const navigate = useNavigate();

  const [confirmModal, setConfirmModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { mutate: downloadTemplate, isPending: isDownloading } =
    useDownloadDeliveryTemplate();

  const { mutate: uploadExcel, isPending: isUploading } =
    useUploadDeliveryExcel(
      () => {
        openNotification(t("deliveries.success"));
        navigate("/deliveries");
      },
      (error) => {
        setErrorMessage(
          translateApiError(error, {
            fallbackKey: "deliveries.form.validations.uploadError",
          })
        );
        setErrorModal(true);
      }
    );

  const handleDownloadSample = () => {
    downloadTemplate(undefined, {
      onSuccess: (response) => {
        // Create a URL for the downloaded file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        // Get filename from the Content-Disposition header or use a default
        const contentDisposition = response.headers["content-disposition"];
        let filename = "delivery_template.xlsx";

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
          );
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, "");
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();

        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      },
      onError: () => {
        setErrorMessage(t("deliveries.form.validations.downloadError"));
        setErrorModal(true);
      },
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e?.target?.files?.[0];

      if (!file) {
        return;
      }

      if (file.type !== XLSX_MIME_TYPE) {
        setFileError(t("deliveries.form.validations.fileTypeError"));
        return;
      }

      setSelectedFile(file);
      setFileName(file.name);
      setFileError("");
    } catch (error) {
      console.error("Error handling file upload:", error);
      setFileError(t("deliveries.form.validations.uploadError"));
    }
  };

  const handleUploadClick = () => {
    try {
      if (!selectedFile) {
        setFileError(t("deliveries.form.validations.uploadError"));
        return;
      }

      setFileError("");
      setConfirmModal(true);
    } catch (error) {
      console.error("Error in form submission:", error);
      setErrorMessage(t("deliveries.form.validations.uploadError"));
      setErrorModal(true);
    }
  };

  const handleConfirmUpload = () => {
    try {
      if (!selectedFile) {
        setFileError(t("deliveries.form.validations.uploadError"));
        setConfirmModal(false);
        return;
      }

      uploadExcel(selectedFile);
      setConfirmModal(false);
    } catch (error) {
      console.error("Error in confirm upload:", error);
      setErrorMessage(t("deliveries.form.validations.uploadError"));
      setErrorModal(true);
      setConfirmModal(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t("deliveries.massive.new")}
        action={
          <Button onClick={handleDownloadSample} disabled={isDownloading}>
            {isDownloading
              ? t("deliveries.massive.downloading")
              : t("deliveries.massive.downloadSample")}
          </Button>
        }
      />

      <div className="new-massive-delivery-form__instructions cds-form-surface">
        <p>{t("deliveries.massive.instructions")}</p>
        <ol>
          <li>{t("deliveries.massive.step1")}</li>
          <li>{t("deliveries.massive.step2")}</li>
          <li>{t("deliveries.massive.step3")}</li>
          <li>{t("deliveries.massive.step4")}</li>
        </ol>
      </div>

      <div className="new-massive-delivery-form__upload cds-form-surface">
        <div className="new-massive-delivery-form__dropzone">
          <DocumentAdd
            size={16}
            className="new-massive-delivery-form__file-icon"
            aria-hidden
          />
          {fileName && (
            <span className="new-massive-delivery-form__file-name">
              {fileName}
            </span>
          )}
          <label
            htmlFor="massiveDeliveryFile"
            className="new-massive-delivery-form__file-cta"
          >
            {isUploading ? (
              <InlineLoading description={t("cta.uploading")} />
            ) : (
              <span>{t("deliveries.form.upload")}</span>
            )}
          </label>
          <input
            id="massiveDeliveryFile"
            name="massiveDeliveryFile"
            type="file"
            className="new-massive-delivery-form__file-input"
            accept=".xlsx"
            onChange={handleFileUpload}
            disabled={isUploading}
          />
          <p className="new-massive-delivery-form__file-hint">XLSX</p>
        </div>
        {fileError && (
          <span className="new-massive-delivery-form__error">
            {fileError}
          </span>
        )}

        <div className="new-massive-delivery-form__actions">
          <Button kind="secondary" as={Link} to="/deliveries">
            {t("cta.cancel")}
          </Button>
          <Button
            onClick={handleUploadClick}
            disabled={isUploading || !fileName}
          >
            {isUploading ? t("cta.uploading") : t("cta.upload")}
          </Button>
        </div>
      </div>

      {confirmModal && (
        <ConfirmDialog
          open={confirmModal}
          title={t("deliveries.massive.new")}
          body={t("deliveries.massive.addQuestion")}
          confirmLabel={t("cta.upload")}
          onConfirm={handleConfirmUpload}
          onCancel={() => setConfirmModal(false)}
          isPending={isUploading}
        />
      )}

      <Modal
        open={errorModal}
        modalHeading={t("deliveries.massive.error")}
        primaryButtonText={t("cta.cancel")}
        onRequestSubmit={() => setErrorModal(false)}
        onRequestClose={() => setErrorModal(false)}
      >
        <p>{errorMessage}</p>
      </Modal>
    </div>
  );
};

export default NewMassiveDeliveryForm;
